import { useState, useEffect } from 'react';
import { Job, JobFilters } from '@/types/job';
import { useJobs } from '@/hooks/useJobs';
import { useAIJobSearch } from '@/hooks/useAIJobSearch';
import { JobCard } from './JobCard';
import { JobDetail } from './JobDetail';
import { JobFilters as JobFiltersComponent } from './JobFilters';
import { AIJobSearch } from './AIJobSearch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Sparkles } from 'lucide-react';

interface JobListProps {
  aiMatchedJobs?: Job[] | null;
  onClearAIResults?: () => void;
  aiReasoning?: string;
  aiConfidence?: number;
  showAISearch?: boolean;
}

export const JobList = ({ aiMatchedJobs, onClearAIResults, aiReasoning, aiConfidence, showAISearch }: JobListProps) => {
  const [filters, setFilters] = useState<JobFilters>({});
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isAIMode, setIsAIMode] = useState(showAISearch || false);
  const [displayJobs, setDisplayJobs] = useState<Job[]>([]);
  const [showingAIResults, setShowingAIResults] = useState(!!aiMatchedJobs);
  
  const { jobs, allJobs, total, isLoading, error } = useJobs(filters);
  const { searchJobs, isSearching, reasoning, confidence, clearSearch } = useAIJobSearch();

  useEffect(() => {
    if (showAISearch) {
      setIsAIMode(true);
      setShowingAIResults(false);
    }
  }, [showAISearch]);

  useEffect(() => {
    if (aiMatchedJobs) {
      setDisplayJobs(aiMatchedJobs);
      setShowingAIResults(true);
      setIsAIMode(false);
    } else if (!isAIMode) {
      setDisplayJobs(jobs);
      setShowingAIResults(false);
    }
  }, [jobs, isAIMode, aiMatchedJobs]);

  const handleAISearch = async (query: string) => {
    const matchedJobs = await searchJobs(allJobs, query);
    setDisplayJobs(matchedJobs);
  };

  const toggleAIMode = () => {
    if (isAIMode) {
      // AI-Modus deaktivieren
      setIsAIMode(false);
      clearSearch();
      setDisplayJobs(jobs);
    } else {
      // AI-Modus aktivieren
      setIsAIMode(true);
      setShowingAIResults(false);
      clearSearch();
      setDisplayJobs(jobs);
      onClearAIResults?.();
    }
  };

  const clearAIResults = () => {
    setIsAIMode(false);
    setShowingAIResults(false);
    clearSearch();
    setDisplayJobs(jobs);
    onClearAIResults?.();
  };

  if (selectedJob) {
    return (
      <JobDetail 
        job={selectedJob} 
        onBack={() => setSelectedJob(null)} 
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <JobFiltersComponent 
          onFiltersChange={setFilters}
          totalJobs={total}
          allJobs={allJobs}
        />
        <Button
          onClick={toggleAIMode}
          variant={isAIMode ? "default" : "outline"}
          className="gap-2"
        >
          <Sparkles className="h-4 w-4" />
          {isAIMode ? 'Klassische Suche' : 'Mit KI suchen'}
        </Button>
      </div>

      {isAIMode && (
        <AIJobSearch
          onSearch={handleAISearch}
          isSearching={isSearching}
          reasoning={reasoning}
          confidence={confidence}
          onClose={toggleAIMode}
        />
      )}
      
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      )}
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-20 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {/* AI Analysis Section */}
          {showingAIResults && aiReasoning && (
            <div className="p-4 sm:p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm sm:text-base font-medium mb-2">Analyse</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {aiReasoning.replace(/\(\d+\)/g, '').trim()}
                  </p>
                  {aiConfidence !== undefined && aiConfidence > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Übereinstimmung:</span>
                      <div className="flex-1 h-2 bg-secondary/20 rounded-full overflow-hidden max-w-md">
                        <div 
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${aiConfidence}%` }}
                        />
                      </div>
                      <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{aiConfidence}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Results Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 border-b border-border/20">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {displayJobs.length} {displayJobs.length === 1 ? 'Stellenangebot' : 'Stellenangebote'}
                {isAIMode && displayJobs.length !== total && (
                  <span className="text-sm text-muted-foreground ml-2">
                    (von {total} gesamt)
                  </span>
                )}
              </h3>
              <p className="text-sm text-muted-foreground">
                Klicken Sie auf eine Stelle für weitere Details
              </p>
            </div>
            
            {/* Clear AI Filter Button - Top */}
            {showingAIResults && aiMatchedJobs && (
              <Button
                onClick={clearAIResults}
                variant="outline"
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Alle {total} Jobs anzeigen
              </Button>
            )}
          </div>
          
          {/* Job Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {displayJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
              />
            ))}
          </div>

          {/* Clear AI Filter Button - Bottom */}
          {showingAIResults && aiMatchedJobs && (
            <div className="flex justify-center pt-6">
              <Button
                onClick={clearAIResults}
                variant="outline"
                size="lg"
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Alle {total} Jobs anzeigen
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};